import { ReactNode } from 'react';

import { CloseButton } from '../../CloseButton';
import { DialogFooter } from '../_DialogFooter';
import { DialogShell } from '../_DialogShell';
import styles from './ConfirmationDialog.module.css';

export interface ConfirmationDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback fired when the dialog is closed (overlay click, escape, close button) */
  onClose: () => void;
  /** Dialog title text */
  title: string;
  /** Primary body text */
  description?: string;
  /** Secondary (dimmed) body text shown below description */
  secondaryText?: string;
  /** Rich body content — replaces `description`/`secondaryText` when provided */
  children?: ReactNode;
  /** Label for the primary (brand) confirm button */
  confirmLabel: string;
  /** Callback fired when confirm is clicked */
  onConfirm?: () => void;
  /** Label for the cancel (tertiary) button — omit to hide */
  cancelLabel?: string;
  /** Callback fired when cancel is clicked */
  onCancel?: () => void;
  /** Label for the left-aligned Back button (tertiary + chevron) — omit to hide */
  backLabel?: string;
  /** Callback fired when Back is clicked */
  onBack?: () => void;
  /** Whether the body scrolls (sticky title + footer) when content overflows */
  isScrollable?: boolean;
  /** Whether the dialog is centered vertically */
  isCentered?: boolean;
  /** Dialog content width */
  size?: string | number;
  /** Whether to close the dialog when clicking outside */
  shouldCloseOnClickOutside?: boolean;
  /** Whether to close the dialog when pressing Escape */
  shouldCloseOnEscape?: boolean;
  className?: string;
}

/**
 * ConfirmationDialog — icon-less confirmation built on the shared Dialog shell.
 * Single brand primary action, optional Cancel and left-aligned Back button,
 * and an optional scrollable body for long content.
 */
export function ConfirmationDialog({
  isOpen,
  onClose,
  title,
  description,
  secondaryText,
  children,
  confirmLabel,
  onConfirm,
  cancelLabel,
  onCancel,
  backLabel,
  onBack,
  isScrollable = false,
  isCentered = true,
  size = 520,
  shouldCloseOnClickOutside,
  shouldCloseOnEscape,
  className,
}: ConfirmationDialogProps) {
  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      isCentered={isCentered}
      size={size}
      shouldCloseOnClickOutside={shouldCloseOnClickOutside}
      shouldCloseOnEscape={shouldCloseOnEscape}
      className={`${styles.root} ${isScrollable ? styles.rootScroll : ''} ${className ?? ''}`}
      contentClassName={isScrollable ? styles.scrollContent : undefined}
    >
      <div className={styles.contentContainer}>
        <div className={styles.titleRow}>
          <p className={styles.title}>{title}</p>
          <CloseButton onClick={onClose} aria-label="Close" />
        </div>
        {(children || description) && (
          <div className={styles.body}>
            {children ?? (
              <>
                <p className={styles.bodyPrimary}>{description}</p>
                {secondaryText && (
                  <p className={styles.bodySecondary}>{secondaryText}</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <DialogFooter
        backLabel={backLabel}
        onBack={onBack}
        secondaryLabel={cancelLabel}
        onSecondaryAction={onCancel}
        primaryLabel={confirmLabel}
        onPrimaryAction={onConfirm}
      />
    </DialogShell>
  );
}

ConfirmationDialog.displayName = 'ConfirmationDialog';
